import { useEffect, useCallback, useRef, useState } from 'react';
import classNames from 'classnames';

import { PRO_FORMA_TABLE_NAV } from 'views/ProFormaTable/constants';
import { formatMilestoneDate } from '../helpers';
import useMilestones from './useMilestones';
import './DevelopmentTimelineBar.scss';

interface DevelopmentTimelineBarProps {
    navigateToSection: (sideNavName: string, tabName?: string) => void;
}

interface LineStyle {
    left: number;
    width: number;
    top: number;
}

const DevelopmentTimelineBar = ({ navigateToSection }: DevelopmentTimelineBarProps) => {
    const [lineStyle, setLineStyle] = useState<LineStyle>({ left: 0, width: 0, top: 0 });
    const milestonesRef = useRef<HTMLDivElement>(null);

    const milestones = useMilestones();
    const shouldAnimate = milestones.length > 0;
    
    const computeLinePosition = useCallback(() => {
        const container = milestonesRef.current;
        if (!container) return;

        const dots = container.querySelectorAll('[data-timeline-dot]');
        if (dots.length < 2) {
            setLineStyle({ left: 0, width: 0, top: 0 });
            return;
        }

        // Finds first and last timeline dot and connects them (vs showing a line from one edge to the other edge)
        const containerRect = container.getBoundingClientRect();
        const firstRect = dots[0].getBoundingClientRect();
        const lastRect = dots[dots.length - 1].getBoundingClientRect();

        const left = firstRect.left + firstRect.width / 2 - containerRect.left;
        const right = lastRect.left + lastRect.width / 2 - containerRect.left;
        const top = firstRect.top + firstRect.height / 2 - containerRect.top - 0.5;

        setLineStyle({ left, width: right - left, top });
    }, []);

    // Milestone data changes
    useEffect(() => {
        computeLinePosition();
    }, [milestones, computeLinePosition]);

    // Window resize
    useEffect(() => {
        const container = milestonesRef.current;
        if (!container) return undefined;

        const observer = new ResizeObserver(computeLinePosition);
        observer.observe(container);
        return () => observer.disconnect();
    }, [computeLinePosition]);

    if (milestones.length === 0) return null;

    const handleMilestoneClick = (tabName: string) => {
        navigateToSection(PRO_FORMA_TABLE_NAV.PRO_FORMA.name, tabName);
    };

    return (
        <div className="DevelopmentTimelineBar d-flex flex-column align-items-start justify-content-center mb-3 py-2 px-3 rounded position-relative">
            <div
                className="DevelopmentTimelineBar__milestones d-flex align-items-start justify-content-between position-relative w-100"
                ref={milestonesRef}
            >
                <div
                    className={classNames('DevelopmentTimelineBar__line position-absolute', {
                        'DevelopmentTimelineBar__line--animate': shouldAnimate,
                    })}
                    style={{ left: lineStyle.left, width: lineStyle.width, top: lineStyle.top }}
                />
                {milestones.map((milestone, index) => (
                    <div
                        key={milestone.label}
                        role="button"
                        tabIndex={0}
                        className={classNames(
                            'DevelopmentTimelineBar__milestone d-flex flex-column align-items-center cursor--pointer text-reset position-relative',
                            { 'DevelopmentTimelineBar__milestone--animate': shouldAnimate },
                        )}
                        style={shouldAnimate ? { '--index': index } as React.CSSProperties : undefined}
                        onClick={() => handleMilestoneClick(milestone.tabName)}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleMilestoneClick(milestone.tabName); }}
                    >
                        <span className="DevelopmentTimelineBar__label font-12 text-center text-uppercase text-nowrap">
                            {milestone.label}
                        </span>
                        <span className="DevelopmentTimelineBar__dot rounded-circle flex-shrink-0 position-relative" data-timeline-dot />
                        <span className="DevelopmentTimelineBar__date font-12 text-center text-nowrap">
                            {formatMilestoneDate(milestone.date)}
                        </span>
                        <span className="DevelopmentTimelineBar__month font-12 text-center text-nowrap mt-n1">
                            {milestone.monthNumber != null ? `Month ${milestone.monthNumber}` : ''}
                        </span>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default DevelopmentTimelineBar;
